/**
 * PanicContactSettings — Contacto de emergencia para el Botón de Pánico.
 *
 * QUÉ HACE: Campo de WhatsApp del dueño usado por `usePanicAlert` para abrir el
 * chat al activar el botón de pánico. Persiste en `businessConfig`
 * (`contactoEmergenciaWhatsapp`) vía `businessConfigService` (ACID). Solo se
 * monta en Opciones avanzadas (dueño/administrador).
 * QUÉ NO DEBE HACER: No envía alertas ni conoce el flujo de pánico.
 */
import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { FloatingLabelInput } from "@/components/ui/FloatingLabelInput";
import { businessConfigService } from "@/features/caja/services/businessConfig.service";
import type { StoredBusinessConfig } from "@/features/caja/utils/businessConfigStorage";
import { isValidWhatsappNumber } from "@/lib/utils/whatsapp";

const SECTION_LABEL =
  "text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2 px-0.5";

export function PanicContactSettings() {
  const [contact, setContact] = useState("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const cfg = businessConfigService.get<Partial<StoredBusinessConfig>>({});
    setContact(cfg.contactoEmergenciaWhatsapp ?? "");
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const id = window.setTimeout(() => {
      const current = businessConfigService.get<Partial<StoredBusinessConfig>>(
        {},
      );
      const next = contact.trim();
      if ((current.contactoEmergenciaWhatsapp ?? "") === next) return;
      businessConfigService.update((cfg) => ({
        ...cfg,
        contactoEmergenciaWhatsapp: next,
      }));
    }, 420);
    return () => window.clearTimeout(id);
  }, [contact, hydrated]);

  const valid = isValidWhatsappNumber(contact);
  const hasValue = contact.trim().length > 0;

  return (
    <div>
      <p className={SECTION_LABEL}>
        Contacto de emergencia para botón de pánico
      </p>
      <FloatingLabelInput
        label="WhatsApp del dueño"
        hint="Ej: +57 300 123 4567"
        icon={<Phone size={14} />}
        inputMode="tel"
        value={contact}
        onChange={(e) => setContact(e.target.value)}
      />
      <p
        className={`mt-1.5 text-[10px] leading-relaxed px-0.5 ${
          hasValue && !valid
            ? "text-red-500 dark:text-red-400 font-medium"
            : "text-gray-500 dark:text-slate-400"
        }`}
      >
        {hasValue && !valid
          ? "Número incompleto: escribe al menos 10 dígitos (con o sin indicativo)."
          : "Número de WhatsApp que se abrirá al activar el botón de pánico. Si queda vacío, el botón avisa que primero debe configurarse."}
      </p>
    </div>
  );
}
